import React, { useEffect, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import {
  FileText, X as XIcon, ChevronUp, ChevronDown, ExternalLink, Calendar, AlertCircle,
} from 'lucide-react';
import axios from '../../../../services/AxiosInstance';
import { getApiUrl } from '../../../../utils/ApiUtils';
import * as payrollApi from '../../../../services/api/payrollApi';
import Callout from '../../Common/Callout';
import { getAbsenceWageTypes } from './wageTypesConfig';
import './payslip-sheet.css';

const ABSENCE_WAGE_TYPES = getAbsenceWageTypes();

const formatCurrency = (amount) => new Intl.NumberFormat('sv-SE', {
  style: 'currency', currency: 'SEK', minimumFractionDigits: 0, maximumFractionDigits: 2,
}).format(Number(amount) || 0);

const formatDate = (value) => (value ? new Date(value).toLocaleDateString('sv-SE') : '');

const periodLabel = (value) => {
  if (!value) return '';
  const label = new Date(value).toLocaleDateString('sv-SE', { month: 'long', year: 'numeric' });
  return label.charAt(0).toUpperCase() + label.slice(1);
};

/**
 * Review-only detail of one payslip, shown in the period page's <SideSheet>
 * when a row in the payslips table is clicked. It answers "is this payslip
 * right?" (rows, totals, warnings) without leaving the payroll run; every
 * edit goes through the payslip page via "Öppna lönebesked".
 *
 * Fetches the same GET /payslip/:id the payslip page uses, keyed on the id
 * so switching rows reloads cleanly. To disable the sheet, revert the row
 * click in MonthlyPayrollPeriod's PayslipsTable to handleRowClick.
 */
export default function PayslipSheet({
  payslipId, companyId, isLocked, onOpen, onClose, onPrev, onNext, position,
}) {
  const [payslip, setPayslip] = useState(null);
  const [vacation, setVacation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!payslipId) return undefined;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setPayslip(null);
    setVacation(null);
    axios.get(getApiUrl(`${companyId}/payslip/${payslipId}`), { withCredentials: true })
      .then((res) => {
        if (cancelled) return;
        const data = res.data?.data || res.data;
        setPayslip(data);
        const employeeId = data?.employeeId || data?.employee_id;
        if (!employeeId) return;
        payrollApi.getVacationBalance(companyId, { employeeId })
          .then((v) => { if (!cancelled) setVacation(v.data?.snapshot || null); })
          .catch(() => { if (!cancelled) setVacation(null); });
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err.response?.data?.message || 'Kunde inte hämta lönespecifikationen.');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [companyId, payslipId]);

  const rows = payslip?.rows || [];
  const employeeName = payslip?.employee?.fullName
    || `${payslip?.employee?.firstname || ''} ${payslip?.employee?.lastname || ''}`.trim();
  const hasBankAccount = Boolean(payslip?.bankAccount?.accountNumber);
  const rowsMissingAbsence = rows.filter((r) => ABSENCE_WAGE_TYPES.includes(r.wageType?.code) && !r.absenceStartDate);
  const advanceDays = Number(vacation?.advanceDaysTaken || 0);
  // An all-zero snapshot (no balance registered) is noise in a review sheet.
  const hasVacationData = Boolean(vacation) && ['remainingPaidDays', 'savedDays', 'usedDays', 'earnedDays', 'carriedOverDays']
    .some((k) => Number(vacation[k] || 0) !== 0);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      {/* Fixed header zone: who, which period, what is paid out. */}
      <div style={{ padding: '16px 24px 22px', borderBottom: '1px solid #f3f4f6', flexShrink: 0 }}>
        <div className="d-flex align-items-center justify-content-between" style={{ gap: 12, marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
            <span style={{
              width: 32, height: 32, borderRadius: 8, flexShrink: 0,
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              background: '#f3f4f6', color: '#6b7280',
            }}>
              <FileText size={16} />
            </span>
            <div style={{ fontSize: '0.8125rem', fontWeight: 600, color: '#111827' }}>Lönespecifikation</div>
          </div>
          <span style={{ fontSize: '0.8125rem', color: '#6b7280', whiteSpace: 'nowrap' }}>
            {payslip ? periodLabel(payslip.periodStartDate) : ''}
          </span>
        </div>
        <div style={{ fontSize: '0.875rem', fontWeight: 600, color: '#111827', overflowWrap: 'anywhere' }}>
          {employeeName || (loading ? 'Hämtar…' : 'Anställd')}
        </div>
        <div style={{ marginTop: 4, display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
          <span style={{ fontSize: '1.75rem', fontWeight: 600, letterSpacing: '-0.01em', color: '#111827', fontVariantNumeric: 'tabular-nums' }}>
            {payslip ? formatCurrency(payslip.netPay) : '–'}
          </span>
          <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>att utbetala</span>
        </div>
      </div>

      <div key={payslipId} className="ll-sheet-body" style={{ padding: '20px 24px 28px', flex: 1, minHeight: 0 }}>
        {loading && (
          <div className="text-center py-4 text-muted">
            <Spinner animation="border" size="sm" className="me-2" />
            Hämtar lönespecifikation…
          </div>
        )}
        {!loading && error && <Callout tone="danger">{error}</Callout>}
        {!loading && payslip && (
          <>
            <div className="pslip-sheet__meta">
              <div>
                <span className="pslip-sheet__label">Löneperiod</span>
                <div className="pslip-sheet__value">{formatDate(payslip.periodStartDate)} – {formatDate(payslip.periodEndDate)}</div>
              </div>
              <div>
                <span className="pslip-sheet__label">Skattetabell</span>
                <div className="pslip-sheet__value">
                  {payslip.contractTaxTable ? `${payslip.contractTaxTable}, kolumn ${payslip.contractTaxColumn ?? '–'}` : '–'}
                </div>
              </div>
              <div>
                <span className="pslip-sheet__label">Utbetalas till</span>
                <div className={`pslip-sheet__value${hasBankAccount ? '' : ' is-muted'}`}>
                  {hasBankAccount
                    ? [payslip.bankAccount.clearingNumber, payslip.bankAccount.accountNumber].filter(Boolean).join('-')
                    : 'Bankkonto saknas'}
                </div>
              </div>
            </div>

            {(!hasBankAccount || rowsMissingAbsence.length > 0 || advanceDays > 0) && (
              <div className="pslip-sheet__warnings">
                {!hasBankAccount && (
                  <Callout tone="warning">Bankkonto saknas för den anställda. Lönen kan inte betalas ut via fil.</Callout>
                )}
                {rowsMissingAbsence.length > 0 && (
                  <Callout tone="warning">
                    {rowsMissingAbsence.length === 1
                      ? `Löneart ${rowsMissingAbsence[0].wageType.code} saknar frånvarodatum.`
                      : `${rowsMissingAbsence.length} lönerader saknar frånvarodatum.`}
                    {' '}Frånvarodatum krävs när uppgiften lämnas till Skatteverket.
                  </Callout>
                )}
                {advanceDays > 0 && (
                  <Callout tone="warning">Förskottssemester: {advanceDays} dagar tagna utöver intjänade.</Callout>
                )}
              </div>
            )}

            <div className="pslip-sheet__rows">
              <div className="pslip-sheet__row-head" aria-hidden="true">
                <span>Löneart</span>
                <span>Benämning</span>
                <span className="pslip-sheet__num">Antal</span>
                <span className="pslip-sheet__num">Belopp</span>
              </div>
              {rows.length === 0 && <div className="pslip-sheet__empty">Inga lönerader ännu.</div>}
              {rows.map((item) => {
                const isDeduction = item.wageType?.effectType === 'dec';
                const missingAbsence = ABSENCE_WAGE_TYPES.includes(item.wageType?.code) && !item.absenceStartDate;
                return (
                  <div className="pslip-sheet__row" key={item.rowId || item.id}>
                    <span className="pslip-sheet__code">{item.wageType?.code}</span>
                    <div className="pslip-sheet__desc">
                      {item.wageType?.description}
                      {(item.comment || (item.absenceStartDate && item.absenceEndDate) || missingAbsence) && (
                        <div className="pslip-sheet__sub">
                          {item.comment && <span>{item.comment}</span>}
                          {item.absenceStartDate && item.absenceEndDate && (
                            <span><Calendar size={11} /> {formatDate(item.absenceStartDate)} – {formatDate(item.absenceEndDate)}</span>
                          )}
                          {missingAbsence && <span className="is-warning"><AlertCircle size={11} /> Frånvarodatum saknas</span>}
                        </div>
                      )}
                    </div>
                    <span className="pslip-sheet__num">{item.quantity} {item.wageType?.unit}</span>
                    <span className={`pslip-sheet__num${isDeduction ? ' is-deduction' : ''}`}>
                      {isDeduction ? '−' : ''}{formatCurrency(Math.abs(item.amount))}
                    </span>
                  </div>
                );
              })}
            </div>

            <dl className="pslip-sheet__totals">
              <dt>Bruttolön</dt><dd>{formatCurrency(payslip.grossPay)}</dd>
              <dt>Preliminär skatt</dt><dd className="is-deduction">−{formatCurrency(payslip.tax)}</dd>
              <dt className="is-net">Att utbetala</dt><dd className="is-net">{formatCurrency(payslip.netPay)}</dd>
              <dt>Arbetsgivaravgifter</dt><dd>{formatCurrency(payslip.socialSecContr)}</dd>
              <dt>Total lönekostnad</dt><dd>{formatCurrency(Number(payslip.grossPay) + Number(payslip.socialSecContr))}</dd>
            </dl>

            {hasVacationData && (
              <div className="pslip-sheet__vacation">
                <span className="pslip-sheet__label">Semester {vacation.year}/{Number(vacation.year) + 1}</span>
                <div className="pslip-sheet__vacation-grid">
                  <div><span>Betalda dagar kvar</span><strong>{Number(vacation.remainingPaidDays || 0)}</strong></div>
                  <div><span>Sparade</span><strong>{Number(vacation.savedDays || 0)}</strong></div>
                  <div><span>Uttagna i år</span><strong>{Number(vacation.usedDays || 0)}</strong></div>
                  <div><span>Intjänade</span><strong>{Number(vacation.earnedDays || 0)}</strong></div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <div className="ll-sheet-footer">
        <div className="ll-sheet-controls">
          <button type="button" className="ll-sheet-btn ll-sheet-btn--primary" onClick={onOpen}>
            <ExternalLink size={14} />
            {isLocked ? 'Visa lönebesked' : 'Öppna lönebesked'}
          </button>
        </div>
        <div className="ll-sheet-controls">
          {position && (
            <>
              <span className="ll-sheet-controls__count">{position.index} av {position.total}</span>
              <span className="ll-sheet-controls__sep" aria-hidden="true" />
            </>
          )}
          <div className="ll-sheet-nav" role="group" aria-label="Bläddra mellan lönebesked">
            <button type="button" onClick={onPrev} disabled={!onPrev} aria-label="Föregående lönebesked" title="Föregående">
              <ChevronUp size={16} />
            </button>
            <button type="button" onClick={onNext} disabled={!onNext} aria-label="Nästa lönebesked" title="Nästa">
              <ChevronDown size={16} />
            </button>
          </div>
          <span className="ll-sheet-controls__sep" aria-hidden="true" />
          <button type="button" className="ll-sheet-close" onClick={onClose} aria-label="Stäng" title="Stäng (Esc)">
            <XIcon size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}
